// src/pages/CRM/SFA/LostReasonAnalytics.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent, IconButton,
  FormControl, InputLabel, Select, MenuItem, Tooltip, CircularProgress,
  Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, Divider, Button, TextField
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { analyticsService } from './sfaService';
import { formatCurrency, LossReasons, OpportunityStageLabels, OpportunityStageColors } from './sfaEnums';

const LostReasonAnalytics = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    date_from: new Date(new Date().setMonth(new Date().getMonth() - 6)),
    date_to: new Date(),
    owner_user_id: ''
  });

  useEffect(() => {
    document.title = 'Lost Reason Analysis | Sales Analytics';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadAnalytics();
    }
  }, [canView, chatbotId, filters]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      setCanView(viewPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const params = {
        chatbot_id: chatbotId,
        date_from: filters.date_from?.toISOString().split('T')[0],
        date_to: filters.date_to?.toISOString().split('T')[0]
      };
      if (filters.owner_user_id) params.owner_user_id = filters.owner_user_id;

      const response = await analyticsService.getLostReasonAnalytics(params);
      if (response.success) {
        setData(response.data);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load analytics', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const getMaxCount = (items, key = 'count') => {
    if (!items || items.length === 0) return 1;
    return Math.max(...items.map(item => item[key] || 0));
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        You don't have permission to view analytics.
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/opportunities`)}>
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                <TrendingDownIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'error.main' }} />
                Lost Reason Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Understand why deals are being lost to improve win rates
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadAnalytics}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <DatePicker
                label="From Date"
                value={filters.date_from}
                onChange={(date) => handleFilterChange('date_from', date)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="To Date"
                value={filters.date_to}
                onChange={(date) => handleFilterChange('date_to', date)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button variant="outlined" onClick={loadAnalytics} fullWidth>
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !data ? (
          <Alert severity="info">No data available for the selected period.</Alert>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      Total Lost Deals
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="error.main" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {data.summary?.total_lost || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      Total Lost Value
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="error.main" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {formatCurrency(data.summary?.total_lost_amount || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      Average Deal Size
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {formatCurrency(data.summary?.avg_deal_size || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      Loss Rate
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="error.main" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {(data.summary?.loss_rate || 0).toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              {/* Lost Reasons Breakdown */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PieChartIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight={600}>
                      Loss Reasons Breakdown
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {data.by_reason && data.by_reason.length > 0 ? (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 700 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Reason</TableCell>
                            <TableCell align="right">Count</TableCell>
                            <TableCell align="right">Value Lost</TableCell>
                            <TableCell sx={{ width: 150 }}>Distribution</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.by_reason.map((item, index) => (
                            <TableRow key={item.reason || index}>
                              <TableCell>
                                <Chip
                                  label={item.reason || 'Not Specified'}
                                  size="small"
                                  color={item.reason === 'Price' ? 'error' :
                                         item.reason === 'Competitor' ? 'warning' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={600}>{item.count}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.total_amount)}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(item.count / getMaxCount(data.by_reason)) * 100}
                                    sx={{ flexGrow: 1, mr: 1, height: 8, borderRadius: 4 }}
                                    color={item.reason === 'Price' ? 'error' : 'primary'}
                                  />
                                  <Typography variant="caption">
                                    {((item.count / (data.summary?.total_lost || 1)) * 100).toFixed(0)}%
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      No loss reason data available
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Lost to Competitors */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BusinessIcon sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="h6" fontWeight={600}>
                      Lost to Competitors
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {data.by_competitor && data.by_competitor.length > 0 ? (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 700 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Competitor</TableCell>
                            <TableCell align="right">Deals Lost</TableCell>
                            <TableCell align="right">Value Lost</TableCell>
                            <TableCell sx={{ width: 150 }}>Impact</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.by_competitor.map((item, index) => (
                            <TableRow key={item.competitor || index}>
                              <TableCell>
                                <Typography fontWeight={500}>
                                  {item.competitor || 'Unknown'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={600}>{item.count}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.total_amount)}
                              </TableCell>
                              <TableCell>
                                <LinearProgress
                                  variant="determinate"
                                  value={(item.total_amount / getMaxCount(data.by_competitor, 'total_amount')) * 100}
                                  sx={{ height: 8, borderRadius: 4 }}
                                  color="warning"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      No competitor data available
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Stage Where Deals Were Lost */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TimelineIcon sx={{ mr: 1, color: 'info.main' }} />
                    <Typography variant="h6" fontWeight={600}>
                      Stage When Lost
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Pipeline stage the opportunity was in before being marked as lost
                  </Typography>

                  {data.by_previous_stage && data.by_previous_stage.length > 0 ? (
                    <Box>
                      {data.by_previous_stage.map((item, index) => (
                        <Box key={item.stage || index} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Chip
                              label={OpportunityStageLabels[item.stage] || item.stage}
                              size="small"
                              sx={{
                                bgcolor: OpportunityStageColors[item.stage] || '#9e9e9e',
                                color: 'white'
                              }}
                            />
                            <Typography variant="body2">
                              {item.count} deals ({formatCurrency(item.total_amount)})
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(item.count / getMaxCount(data.by_previous_stage)) * 100}
                            sx={{ height: 10, borderRadius: 5 }}
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      No stage data available
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Monthly Trend */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MoneyIcon sx={{ mr: 1, color: 'error.main' }} />
                    <Typography variant="h6" fontWeight={600}>
                      Monthly Loss Trend
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {data.monthly_trend && data.monthly_trend.length > 0 ? (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 700 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Month</TableCell>
                            <TableCell align="right">Deals Lost</TableCell>
                            <TableCell align="right">Value Lost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.monthly_trend.map((item, index) => (
                            <TableRow key={item.month || index}>
                              <TableCell>{item.month}</TableCell>
                              <TableCell align="right">{item.count}</TableCell>
                              <TableCell align="right">{formatCurrency(item.total_amount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      No trend data available
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default LostReasonAnalytics;
